import { collabStateManager } from '@databases/collab-state-manager';
import { useDatabaseContent } from '@databases/database-state';
import { type DatabaseJSON } from '@databases/model/database';
import { type FieldType } from '@databases/model/field';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { appStoreUtils } from '@databases/redux/store';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import useView from '@databases/views/useView';
import { useEffect } from 'react';

import { useAnalytics } from './useAnalytics';
import { getDatabaseContainerType } from './utils';

export interface DatabaseSizeMetrics {
  entriesCount: number | false;
  fieldsCount: number | false;
  fieldTypeCounts: Record<FieldType, number> | false;
  fieldTypes: FieldType[] | false;
  viewsCount: number | false;
}

/**
 * These metrics are NOT reactive. It does not change as the databases is edited.
 * Over time, we can have this update if we really need to. But for now this is to help
 * getting a general size of the database for certain metrics.
 */
let databaseSizeMetrics: DatabaseSizeMetrics = {
  entriesCount: false,
  fieldsCount: false,
  fieldTypeCounts: false,
  fieldTypes: false,
  viewsCount: false,
};

/**
 * Get the cached database size metrics for tracking metrics
 * relative to the size of the current database.
 */
export function getDatabaseSizeMetrics(): DatabaseSizeMetrics {
  return databaseSizeMetrics;
}

function setDatabaseSizeMetrics(metrics: DatabaseSizeMetrics) {
  databaseSizeMetrics = {
    entriesCount: metrics.entriesCount,
    fieldsCount: metrics.fieldsCount,
    fieldTypeCounts: metrics.fieldTypeCounts,
    fieldTypes: metrics.fieldTypes,
    viewsCount: metrics.viewsCount,
  };
}

/**
 * Tracks database metrics for the current database.
 * This will fire whenever the database is loaded and ready or when the contentId changes.
 */
export const useTrackDatabaseMetrics = () => {
  const { contentId } = useDatabaseContent();
  const { view } = useView();
  const analytics = useAnalytics();

  const manager = collabStateManager.getCollabManager(contentId);

  const databaseStatus = appStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  function getEntriesCount(database: DatabaseJSON) {
    return database.entries.length;
  }

  function getFieldsCount(database: DatabaseJSON) {
    return database.fields.length;
  }

  function getViewsCount(database: DatabaseJSON) {
    return database.views.length;
  }

  function getFieldTypeCounts(database: DatabaseJSON) {
    const fieldTypeCounts = new Map<FieldType, number>();

    Object.keys(database.fieldsById).forEach((id) => {
      const type = database.fieldsById[id].type;

      if (fieldTypeCounts.has(type)) {
        fieldTypeCounts.set(type, (fieldTypeCounts.get(type) ?? 0) + 1);
      } else {
        fieldTypeCounts.set(type, 1);
      }
    });

    return Object.fromEntries(fieldTypeCounts) as Record<FieldType, number>;
  }

  function getFieldTypes(database: DatabaseJSON) {
    return Array.from(new Set(Object.keys(getFieldTypeCounts(database)))) as FieldType[];
  }

  function getViewSettings() {
    return {
      layoutType: view.settings.layout.type,
      showNumberedEntriesEnabled: view.settings.layout.showNumberedEntries,
      cardWidth: view.settings.layout.cardWidth,
    };
  }

  useEffect(() => {
    if (
      !contentId ||
      databaseStatus?.status !== AsyncOperationStatus.SUCCEEDED ||
      !manager
    ) {
      return;
    }

    const fire = () => {
      const jsonData = manager.database.toJSON();
      const attributes = {
        fieldsCount: getFieldsCount(jsonData),
        fieldTypeCounts: getFieldTypeCounts(jsonData),
        fieldTypes: getFieldTypes(jsonData),
        entriesCount: getEntriesCount(jsonData),
        viewsCount: getViewsCount(jsonData),
        viewSettings: getViewSettings(),
      };

      // set the database metrics once, when this fires.
      setDatabaseSizeMetrics(attributes);

      analytics.sendDatabaseProfiledEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        attributes,
      });
    };

    fire();

    // this effect should only run when the database is loaded and ready or when the contentId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseStatus?.status, contentId, manager]);
};
